import { useState, useMemo, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, CalendarDays, AlertCircle, RefreshCw, Search, Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { Badge } from '../components/ui/badge'
import { Label } from '../components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog'
import { useQuarterlyReportsList, useCreateQuarterlyReport } from '../hooks/useQuarterlyReports'
import { useAdminUsers } from '../hooks/useAdmin'
import { QuarterlyReport, QuarterlyReportStatus } from '../types/quarterlyReport'
import { useAuthStore } from '../stores/authStore'
import { toast } from '../hooks/useToast'
import { getApiError } from '../lib/api'

const statusKeys: Record<QuarterlyReportStatus, string> = {
  draft: 'quarterlyReports.status.draft',
  pending_payment: 'quarterlyReports.status.pendingPayment',
  finalized: 'quarterlyReports.status.finalized',
  archived: 'quarterlyReports.status.archived',
}

const statusVariants: Record<QuarterlyReportStatus, string> = {
  draft: 'secondary',
  pending_payment: 'warning',
  finalized: 'success',
  archived: 'default',
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount)
}

export default function QuarterlyReports() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const isAdmin = user?.role === 'admin'

  const currentYear = new Date().getFullYear()
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Create form state
  const [createUserId, setCreateUserId] = useState<number | null>(null)
  const [createYear, setCreateYear] = useState<number>(currentYear)
  const [createQuarter, setCreateQuarter] = useState<number>(1)
  const [userSearch, setUserSearch] = useState('')
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const userSearchRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const { data: reports = [], isLoading, error, refetch } = useQuarterlyReportsList({ year: selectedYear })
  const createMutation = useCreateQuarterlyReport()

  // Fetch admin users for the dropdown
  const { data: usersData } = useAdminUsers({ limit: 100, search: userSearch || undefined })
  const usersList = useMemo(() => usersData?.users ?? [], [usersData])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedUser = useMemo(
    () => usersList.find(u => u.id === createUserId),
    [usersList, createUserId]
  )

  const handleOpenCreateDialog = () => {
    setCreateUserId(isAdmin ? null : (user?.id ?? null))
    setCreateYear(currentYear)
    setCreateQuarter(1)
    setUserSearch('')
    setUserDropdownOpen(false)
    setDialogOpen(true)
  }

  const handleCreate = async () => {
    const userId = isAdmin ? createUserId : (user?.id ?? 0)
    if (!userId) return

    try {
      await createMutation.mutateAsync({
        userId,
        year: createYear,
        quarter: createQuarter,
      })
      setDialogOpen(false)
      toast({
        title: t('quarterlyReports.toast.created'),
        description: t('quarterlyReports.toast.createdDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleCardClick = (report: QuarterlyReport) => {
    navigate(`/dashboard/quarterly-reports/${report.id}`)
  }

  // Error state
  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('quarterlyReports.errorLoading')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('quarterlyReports.title')}</h1>
        </div>
        <div className="flex items-center gap-3">
          {/* Year filter */}
          <div className="w-32">
            <Select
              value={String(selectedYear)}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </Select>
          </div>

          {/* Create button (admin only) */}
          {isAdmin && (
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {t('quarterlyReports.create')}
            </Button>
          )}
        </div>
      </div>

      {/* Reports grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-24 mb-2" />
                <Skeleton className="h-5 w-16" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <CalendarDays className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-semibold">{t('quarterlyReports.noReports')}</h3>
          <p className="text-muted-foreground mb-4">
            {t('quarterlyReports.noReportsDesc')}
          </p>
          {isAdmin && (
            <Button onClick={handleOpenCreateDialog}>
              <Plus className="mr-2 h-4 w-4" />
              {t('quarterlyReports.create')}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {reports.map((report) => (
            <Card
              key={report.id}
              className="cursor-pointer transition-shadow hover:shadow-md"
              onClick={() => handleCardClick(report)}
            >
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
                <CardTitle className="text-lg">
                  Q{report.quarter} {report.year}
                </CardTitle>
                <Badge variant={statusVariants[report.status] as any}>
                  {t(statusKeys[report.status])}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {/* Invoice summary */}
                  {report.invoiceSummary && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t('quarterlyReports.outgoing')}
                        </span>
                        <span className="font-medium">
                          {report.invoiceSummary.outgoingCount} / {formatCurrency(report.invoiceSummary.outgoingTotal)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t('quarterlyReports.incoming')}
                        </span>
                        <span className="font-medium">
                          {report.invoiceSummary.incomingCount} / {formatCurrency(report.invoiceSummary.incomingTotal)}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Charge amount */}
                  {report.chargeAmount != null && (
                    <div className="flex items-center justify-between pt-1 border-t">
                      <span className="text-muted-foreground">
                        {t('quarterlyReports.chargeAmount')}
                      </span>
                      <span className="font-semibold">
                        {formatCurrency(report.chargeAmount)}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('quarterlyReports.createTitle')}</DialogTitle>
            <DialogDescription>
              {t('quarterlyReports.createDesc')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* User selector (admin only) */}
            {isAdmin && (
              <div className="space-y-2">
                <Label>{t('quarterlyReports.selectUser')}</Label>
                <div className="relative" ref={dropdownRef}>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between font-normal"
                    onClick={() => {
                      setUserDropdownOpen(!userDropdownOpen)
                      setTimeout(() => userSearchRef.current?.focus(), 50)
                    }}
                  >
                    {selectedUser ? (
                      <span className="truncate">
                        {selectedUser.name} ({selectedUser.email})
                      </span>
                    ) : (
                      <span className="text-muted-foreground">{t('quarterlyReports.selectUserPlaceholder')}</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                  {userDropdownOpen && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            ref={userSearchRef}
                            placeholder={t('quarterlyReports.searchUsers')}
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            className="pl-8 h-9"
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto p-1">
                        {usersList.length === 0 ? (
                          <p className="py-3 text-center text-sm text-muted-foreground">
                            {t('admin.users.noUsers')}
                          </p>
                        ) : (
                          usersList.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
                              onClick={() => {
                                setCreateUserId(u.id)
                                setUserDropdownOpen(false)
                              }}
                            >
                              {createUserId === u.id ? (
                                <Check className="h-4 w-4 shrink-0" />
                              ) : (
                                <span className="h-4 w-4 shrink-0" />
                              )}
                              <span className="truncate font-medium">{u.name}</span>
                              <span className="truncate text-muted-foreground">{u.email}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Year */}
            <div className="space-y-2">
              <Label htmlFor="create-year">
                {t('quarterlyReports.year')}
              </Label>
              <Select
                id="create-year"
                value={String(createYear)}
                onChange={(e) => setCreateYear(Number(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>

            {/* Quarter */}
            <div className="space-y-2">
              <Label htmlFor="create-quarter">
                {t('quarterlyReports.quarter')}
              </Label>
              <Select
                id="create-quarter"
                value={String(createQuarter)}
                onChange={(e) => setCreateQuarter(Number(e.target.value))}
              >
                <option value="1">Q1</option>
                <option value="2">Q2</option>
                <option value="3">Q3</option>
                <option value="4">Q4</option>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createMutation.isPending || (isAdmin && createUserId === null)}
            >
              {createMutation.isPending && (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('quarterlyReports.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
